import { parseLocalDateString } from './parseLocalDate';

export const BOOKING_TIME_ZONE = 'Europe/Berlin';
export const BOOKING_LOCALE = 'de-DE';

const toValidDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const timeZonePartsFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: BOOKING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});

const getTimeZoneParts = (value) => {
    const date = toValidDate(value);
    if (!date) return null;

    const parts = Object.fromEntries(
        timeZonePartsFormatter
            .formatToParts(date)
            .filter(({ type }) => type !== 'literal')
            .map(({ type, value: partValue }) => [type, Number(partValue)])
    );

    return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
    };
};

const getTimeZoneOffsetMs = (date) => {
    const parts = getTimeZoneParts(date);
    if (!parts) return null;

    const wallTimeAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
    );
    return date.getTime() - wallTimeAsUtc;
};

const matchesWallTime = (date, expected) => {
    const actual = getTimeZoneParts(date);
    return Boolean(actual) && Object.keys(expected).every(
        (key) => actual[key] === expected[key]
    );
};

const getBerlinParts = (value = new Date()) => {
    const date = toValidDate(value);
    if (!date) return null;

    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: BOOKING_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        weekday: 'short',
    }).formatToParts(date);

    const read = (type) => parts.find((part) => part.type === type)?.value;

    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: Number(read('hour')),
        minute: Number(read('minute')),
        second: Number(read('second')),
        weekday: read('weekday'),
    };
};

const WEEKDAY_TO_JS = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
};

export const getBerlinDayKey = (value) => {
    return formatBerlinDate(value, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
};

export const getBerlinTodayDayKey = () => getBerlinDayKey(new Date());

export const getBerlinTodayDateString = () => {
    const parts = getBerlinParts(new Date());
    if (!parts) return '';
    return `${parts.year}-${parts.month}-${parts.day}`;
};

export const toDateStringFromDayKey = (dayKey) => {
    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(dayKey || '').trim());
    if (!match) return null;
    return `${match[3]}-${match[2]}-${match[1]}`;
};

export const toDayKeyFromDateString = (dateStr) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || '').trim());
    if (!match) return getBerlinDayKey(dateStr);
    return `${match[3]}.${match[2]}.${match[1]}`;
};

export const formatDateStringFromBerlinInstant = (value) => {
    const parts = getBerlinParts(value);
    if (!parts) return '';
    return `${parts.year}-${parts.month}-${parts.day}`;
};

export const addDaysToDateString = (dateStr, dayCount) => {
    const base = parseBerlinDateTime(dateStr, '12:00');
    if (!base) return dateStr;
    const shifted = new Date(base.getTime() + dayCount * 24 * 60 * 60 * 1000);
    return formatDateStringFromBerlinInstant(shifted);
};

export const parseBerlinDateTime = (dateStr, timeStr = '00:00') => {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || '').trim());
    if (!dateMatch) return null;

    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(String(timeStr || '00:00').trim());
    if (!timeMatch) return null;

    const year = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const day = Number(dateMatch[3]);
    let hour = Number(timeMatch[1]);
    const minute = Number(timeMatch[2]);

    if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        hour < 0 ||
        hour > 24 ||
        minute < 0 ||
        minute > 59 ||
        (hour === 24 && minute !== 0)
    ) {
        return null;
    }

    const calendarProbe = new Date(Date.UTC(year, month - 1, day));
    if (
        calendarProbe.getUTCFullYear() !== year ||
        calendarProbe.getUTCMonth() !== month - 1 ||
        calendarProbe.getUTCDate() !== day
    ) {
        return null;
    }

    let dayOffset = 0;

    if (hour === 24) {
        hour = 0;
        dayOffset = 1;
    }

    const wallUtc = Date.UTC(year, month - 1, day + dayOffset, hour, minute, 0);
    const normalizedWallDate = new Date(wallUtc);
    const expectedWallTime = {
        year: normalizedWallDate.getUTCFullYear(),
        month: normalizedWallDate.getUTCMonth() + 1,
        day: normalizedWallDate.getUTCDate(),
        hour: normalizedWallDate.getUTCHours(),
        minute: normalizedWallDate.getUTCMinutes(),
        second: 0,
    };

    // Probe both sides of a possible DST transition. Ambiguous wall times
    // intentionally resolve to the earlier instant; nonexistent times fail.
    const offsetCandidates = [
        wallUtc - 36 * 60 * 60 * 1000,
        wallUtc,
        wallUtc + 36 * 60 * 60 * 1000,
    ]
        .map((probeMs) => getTimeZoneOffsetMs(new Date(probeMs)))
        .filter((offsetMs) => Number.isFinite(offsetMs));

    const matchingInstants = [...new Set(offsetCandidates)]
        .map((offsetMs) => new Date(wallUtc + offsetMs))
        .filter((candidate) => matchesWallTime(candidate, expectedWallTime))
        .sort((left, right) => left.getTime() - right.getTime());

    return matchingInstants[0] || null;
};

export const getBerlinWeekday = (dateStr) => {
    const instant = parseBerlinDateTime(dateStr, '12:00');
    if (!instant) return null;
    const parts = getBerlinParts(instant);
    return WEEKDAY_TO_JS[parts?.weekday] ?? null;
};

export const isBerlinTodayDateString = (dateStr) => {
    const dayKey = toDayKeyFromDateString(dateStr);
    return Boolean(dayKey) && dayKey === getBerlinTodayDayKey();
};

export const isBerlinToday = (value) => {
    const date = toValidDate(value);
    if (!date) return false;
    return getBerlinDayKey(date) === getBerlinTodayDayKey();
};

export const isBerlinSameDay = (left, right) => {
    const leftKey = getBerlinDayKey(left);
    const rightKey = getBerlinDayKey(right);
    return Boolean(leftKey) && leftKey === rightKey;
};

export const isSlotBeforeBerlinNow = (dateStr, timeStr, bufferMinutes = 30) => {
    const slot = parseBerlinDateTime(dateStr, timeStr);
    if (!slot) return false;
    const bufferMs = bufferMinutes * 60 * 1000;
    return slot.getTime() <= Date.now() + bufferMs;
};

export const hasBookingStartPassed = (startDateTime) => {
    const start = toValidDate(startDateTime);
    if (!start) return false;
    return start.getTime() <= Date.now();
};

export const isBookingBerlinToday = (startDateTime) => {
    return getBerlinDayKey(startDateTime) === getBerlinTodayDayKey();
};

export const getBerlinTodayForPicker = () => parseLocalDateString(getBerlinTodayDateString());

export const isCompletedBookingStatus = (status) => {
    return String(status || '').toUpperCase() === 'COMPLETED';
};

export const formatBerlinDate = (value, options = {}) => {
    const date = toValidDate(value);
    if (!date) return '';

    return date.toLocaleDateString(BOOKING_LOCALE, {
        ...options,
        timeZone: BOOKING_TIME_ZONE,
    });
};

export const formatBerlinTime = (value, options = {}) => {
    const date = toValidDate(value);
    if (!date) return '';

    return date.toLocaleTimeString(BOOKING_LOCALE, {
        ...options,
        timeZone: BOOKING_TIME_ZONE,
    });
};

export const formatBerlinDateTimeRange = (
    startValue,
    endValue,
    {
        dateOptions = {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        },
        timeOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        },
        sameDaySeparator = ' - ',
        timeSeparator = ' - ',
        timeSuffix = ' Uhr',
    } = {}
) => {
    const start = toValidDate(startValue);
    const end = toValidDate(endValue);

    if (!start || !end) return null;

    const startDate = formatBerlinDate(start, dateOptions);
    const endDate = formatBerlinDate(end, dateOptions);
    const startTime = formatBerlinTime(start, timeOptions);
    const endTime = formatBerlinTime(end, timeOptions);
    const isSameDay = getBerlinDayKey(start) === getBerlinDayKey(end);

    return {
        date: isSameDay ? startDate : `${startDate}${sameDaySeparator}${endDate}`,
        time: `${startTime}${timeSeparator}${endTime}${timeSuffix}`,
        startDate,
        endDate,
        startTime,
        endTime,
        isSameDay,
    };
};
